import {
  Link,
  Box,
  Button,
  FormControl,
  Grid,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import MUIDataTable from "mui-datatables";
import IconButton from "@mui/material/IconButton";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import SearchIcon from "@mui/icons-material/Search";
import RemoveIcon from "@mui/icons-material/Remove";
import CleaningServicesIcon from "@mui/icons-material/CleaningServices";
import { useEffect, useState } from "react";
import { getHistorialIndice, getHistorialIndiceCompleto, getIndices } from "../../../services/IndiceService";
import IndiceDialog from "./IndiceDialog";
import Message from "../../../utils/Message";

const nameItemsText = "Índices";

const initialSearchData = {
  codigo: "",
  descripcion: "",
  estado: "habilitado",
  tipoValor: "todo",
  tipoFecha: "todo",
  fechaDesde: "",
  fechaHasta: "",
  sortField: "descripcion",
  sortOrder: "ASC",
};

const estadoOptions = [
  { id: "todo", descripcion: "Todo" },
  { id: "habilitado", descripcion: "Habilitado" },
  { id: "deshabilitado", descripcion: "Deshabilitado" },
];

const tipoFechaOptions = [
  { id: "todo", descripcion: "Todo" },
  { id: "DIARIO", descripcion: "Diario" },
  { id: "MENSUAL", descripcion: "Mensual" },
  { id: "VIGENCIA", descripcion: "Vigencia" },
];

const ordenarPorOptions = [
  { id: "descripcion", descripcion: "Descripción" },
  { id: "codigo", descripcion: "Código" },
  { id: "tipo_valor", descripcion: "Tipo valor" },
  { id: "tipo_fecha", descripcion: "Tipo fecha" },
  { id: "valor", descripcion: "Valor" },
  { id: "fecha_actualizacion", descripcion: "Última actualización" },
];

const ordenOptions = [
  { id: "ASC", descripcion: "Ascendente" },
  { id: "DESC", descripcion: "Descendente" },
];

const formatValor = (item) => {
  if (!item || item.valor === null || item.valor === undefined) {
    return "";
  }
  const valor = Number(item.valor);
  const valorConDosDecimales = Number.isNaN(valor) ? item.valor : valor.toFixed(2);
  return item.tipo_valor === "PORCENTAJE"
    ? `${valor}%`
    : `${valorConDosDecimales}` + (item.tipo_valor ? ' - ' + item.tipo_valor : "");
};

const formatPeriodo = (item) => {
  if (!item) {
    return "";
  }
  if (item.tipo_fecha === "VIGENCIA") {
    return `${item.vigencia_desde || ""} ${item.vigencia_hasta ? `a ${item.vigencia_hasta}` : ""}`.trim();
  }
  if (item.tipo_fecha === "MENSUAL") {
    const mes = item.fecha ? String(item.fecha).slice(3, 5) : "";
    const anio = item.fecha ? String(item.fecha).slice(6, 10) : "";
    return `${mes}-${anio}`.trim();
  }
  if (item.tipo_fecha === "DIARIO") {
    return item.fecha ? String(item.fecha).slice(0, 10) : "";
  }
  return "";
};

const IndiceTable = () => {
  const [itemsCrud, setItemsCrud] = useState([]);
  const [itemCrud, setItemCrud] = useState({});
  const [actionCrud, setActionCrud] = useState("");
  const [isDoingSomething, setIsDoingSomething] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [totalRowsCount, setTotalRowsCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [message, setMessage] = useState({});
  const [showMessage, setShowMessage] = useState(false);
  const [searchData, setSearchData] = useState(initialSearchData);
  const [historialPorIndice, setHistorialPorIndice] = useState({});
  const [historialCompletoCargadoPorIndice, setHistorialCompletoCargadoPorIndice] = useState({});
  const [historialLoadingPorIndice, setHistorialLoadingPorIndice] = useState({});
  const [historialErrorPorIndice, setHistorialErrorPorIndice] = useState({});

  const load = async () => {
    const params = {
      descripcion: searchData.descripcion,
      estado: searchData.estado,
      codigo: searchData.codigo,
      tipoValor: searchData.tipoValor,
      tipoFecha: searchData.tipoFecha,
      fechaDesde: searchData.fechaDesde ? new Date(searchData.fechaDesde) : null,
      fechaHasta: searchData.fechaHasta ? new Date(searchData.fechaHasta) : null,
      sortField: searchData.sortField,
      sortOrder: searchData.sortOrder,
      rows: rowsPerPage,
      first: currentPage * rowsPerPage,
    };

    setIsDoingSomething(true);
    const response = await getIndices(params);
    setIsDoingSomething(false);

    if (response.ok) {
      setTotalRowsCount(response.data.count || 0);
      setItemsCrud(response.data.data || []);
      setHistorialPorIndice({});
      setHistorialCompletoCargadoPorIndice({});
      setHistorialLoadingPorIndice({});
      setHistorialErrorPorIndice({});
      return;
    }

    setMessage({
      messageText: response.messageText,
      severity: response.messageSeverity,
    });
    setShowMessage(true);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, rowsPerPage]);

  const handleChange = (e) => {
    setSearchData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleLimpiar = () => {
    setSearchData(initialSearchData);
    setCurrentPage(0);
  };

  const handleBuscar = () => {
    setCurrentPage(0);
    load();
  };

  const loadHistorialForRow = async (item, loadAll = false, forceReload = false) => {
    if (!item?.id || historialLoadingPorIndice[item.id]) {
      return;
    }
    if (!forceReload && !loadAll && historialPorIndice[item.id]) {
      return;
    }
    if (!forceReload && loadAll && historialCompletoCargadoPorIndice[item.id]) {
      return;
    }

    setHistorialLoadingPorIndice((prev) => ({ ...prev, [item.id]: true }));
    setHistorialErrorPorIndice((prev) => ({ ...prev, [item.id]: "" }));

    const params = {
      id: item.id,
      sortfield: "fecha",
      sortorder: "DESC",
    };
    if (!loadAll) {
      params.rows = 3;
      params.first = 0;
    } else {
      params.rows = 1000;
      params.first = 0;
    }
    const response = await getHistorialIndice(params);

    if (response.ok) {
      const historial = response?.data?.data || [];
      setHistorialPorIndice((prev) => ({ ...prev, [item.id]: historial }));
      setHistorialCompletoCargadoPorIndice((prev) => ({ ...prev, [item.id]: !!loadAll }));
    } else {
      setHistorialErrorPorIndice((prev) => ({
        ...prev,
        [item.id]: response.messageText || "No se pudo obtener el historial",
      }));
    }

    setHistorialLoadingPorIndice((prev) => ({ ...prev, [item.id]: false }));
  };

  const handleClickActionCrud = (item, action) => {
    if (action === "create") {
      setActionCrud(action);
      setItemCrud({});
      setOpenDialog(true);
      return;
    }

    if (action === "update") {
      setActionCrud(action);
      setItemCrud({
        ...item,
        valor: "",
        fecha: "",
        vigencia_desde: "",
        vigencia_hasta: "",
        referencia: "",
        resolucion: "",
      });
      setOpenDialog(true);
      return;
    }

    if (action === "edit") {
      setActionCrud(action);
      setItemCrud({
        ...item,
      });
      setOpenDialog(true);
      return;
    }

    setActionCrud(action);
    setItemCrud(item);
    setOpenDialog(true);
  };

  const tableColumns = [
    { name: "id", label: "ID", options: { display: "excluded" } },
    { name: "codigo", label: "Código", options: { filter: false, sort: false } },
    { name: "descripcion", label: "Descripción", options: { filter: false, sort: false } },
    {
      name: "valor",
      label: "Último valor",
      options: {
        filter: false,
        sort: false,
        customBodyRenderLite: (dataIndex) => formatValor(itemsCrud[dataIndex]),
      },
    },

    { name: "tipo_fecha", label: "Actualización", options: { filter: false, sort: false } },

    {
      name: "periodo",
      label: "Período",
      options: {
        filter: false,
        sort: false,
        customBodyRenderLite: (dataIndex) => formatPeriodo(itemsCrud[dataIndex]),
      },
    },
    {
      name: "fuente",
      label: "Fuente",
      options: {
        filter: false,
        sort: false,
        customBodyRenderLite: (dataIndex) => {
          const fuenteNombre = itemsCrud[dataIndex]?.fuente_nombre;
          const url = itemsCrud[dataIndex]?.fuente_url;
          if (!fuenteNombre && !url) {
            return "";
          }
          if (!url) {
            return fuenteNombre;
          }
          if (!fuenteNombre) {
            return (
              <Link href={url} target="_blank" rel="noopener noreferrer">
                {url}
              </Link>
            );
          }
          return (
            <>
              {fuenteNombre} (
              <Link href={url} target="_blank" rel="noopener noreferrer">
                {url}
              </Link>
              )
            </>
          );
        },
      },
    },
    {
      name: "actions",
      label: "Acciones",
      options: {
        filter: false,
        sort: false,
        empty: true,
        customBodyRenderLite: (dataIndex) => {
          const row = itemsCrud[dataIndex];
          return (
            <>
              <Tooltip title="Nuevo valor">
                <IconButton
                  size="small"
                  aria-label="new-history-value"
                  onClick={() => handleClickActionCrud(row, "update")}
                >
                  <AddIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Editar índice">
                <IconButton
                  size="small"
                  aria-label="edit-index"
                  onClick={() => handleClickActionCrud(row, "edit")}
                >
                  <EditIcon />
                </IconButton>
              </Tooltip>
              {row.habilitado ? (
                <Tooltip title="Deshabilitar">
                  <IconButton
                    size="small"
                    aria-label="disable"
                    onClick={() => handleClickActionCrud(row, "disable")}
                  >
                    <RemoveIcon />
                  </IconButton>
                </Tooltip>
              ) : (
                <Tooltip title="Habilitar">
                  <IconButton
                    size="small"
                    aria-label="enable"
                    onClick={() => handleClickActionCrud(row, "enable")}
                  >
                    <AddIcon />
                  </IconButton>
                </Tooltip>
              )}
            </>
          );
        },
      },
    },
  ];

  const tableOptions = {
    rowsPerPageOptions: [10, 20, 30],
    serverSide: true,
    count: totalRowsCount,
    rowsPerPage,
    onChangeRowsPerPage: (numberOfRows) => setRowsPerPage(numberOfRows),
    onChangePage: (page) => setCurrentPage(page),
    download: false,
    filter: false,
    print: false,
    search: false,
    viewColumns: false,
    selectableRowsHeader: false,
    selectableRows: "none",
    expandableRows: true,
    expandableRowsHeader: false,
    onRowExpansionChange: (_currentRowsExpanded, allRowsExpanded) => {
      const expandedDataIndexes = (allRowsExpanded || [])
        .map((row) => (typeof row?.dataIndex === "number" ? row.dataIndex : null))
        .filter((index) => index !== null);

      expandedDataIndexes.forEach((dataIndex) => {
        const rowItem = itemsCrud[dataIndex];
        if (rowItem) {
          loadHistorialForRow(rowItem, false);
        }
      });
    },
    renderExpandableRow: (_rowData, rowMeta) => {
      const item = itemsCrud[rowMeta.dataIndex];
      const historial = item?.id ? historialPorIndice[item.id] || [] : [];
      const historialCompleto = item?.id ? !!historialCompletoCargadoPorIndice[item.id] : false;
      const isLoadingHistorial = item?.id ? historialLoadingPorIndice[item.id] : false;
      const historialError = item?.id ? historialErrorPorIndice[item.id] : "";

      return (
        <TableRow>
          <TableCell colSpan={tableColumns.length + 1}>
            <Box sx={{ px: 2, py: 1 }}>
              {isLoadingHistorial && <LinearProgress sx={{ mb: 1 }} />}
              {!isLoadingHistorial && historialError && (
                <Typography color="error" variant="body2">
                  {historialError}
                </Typography>
              )}
              {!isLoadingHistorial && !historialError && historial.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                  Sin historial disponible
                </Typography>
              )}

              {!isLoadingHistorial && !historialError && historial.length > 0 && (
                <>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Fecha registro</TableCell>
                        <TableCell>Valor</TableCell>
                        <TableCell>Período</TableCell>
                        <TableCell>Referencia</TableCell>
                        <TableCell>Resolución</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {historial.map((historialItem, historialIndex) => (
                        <TableRow key={`${item.id}-${historialItem.id || historialIndex}`}>
                          <TableCell>{historialItem.fecha_creacion || ""}</TableCell>
                          <TableCell>{formatValor(historialItem)}</TableCell>
                          <TableCell>{formatPeriodo({ ...item, ...historialItem })}</TableCell>
                          <TableCell>{historialItem.referencia || ""}</TableCell>
                          <TableCell>{historialItem.resolucion || ""}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>

                  {!historialCompleto && (
                    <Box sx={{ mt: 1 }}>
                      <Button
                        size="small"
                        variant="text"
                        onClick={() => loadHistorialForRow(item, true)}
                        sx={{ p: 0, minWidth: "auto", textTransform: "none" }}
                      >
                        Ver historial completo
                      </Button>
                    </Box>
                  )}
                  {historialCompleto && (
                    <Box sx={{ mt: 1 }}>
                      <Button
                        size="small"
                        variant="text"
                        onClick={() => loadHistorialForRow(item, false, true)}
                        sx={{ p: 0, minWidth: "auto", textTransform: "none" }}
                      >
                        Cerrar historial completo
                      </Button>
                    </Box>
                  )}
                </>
              )}
            </Box>
          </TableCell>
        </TableRow>
      );
    },
    elevation: 1,
    textLabels: {
      body: { noMatch: "No se han encontrado resultados para esta búsqueda" },
      pagination: { next: "Próxima", previous: "Previa", rowsPerPage: "Filas:", displayRows: "de" },
    },
  };

  const handleDialogSave = () => {
    load();
    setOpenDialog(false);
  };

  return (
    <>
      <Paper elevation={2}>
        <Box sx={{ margin: "auto", width: "90%" }}>
          <Box sx={{ m: 1, p: 1 }}>
            <Grid container spacing={1}>
              <Grid item xs={12}>
                <h2>{nameItemsText}</h2>
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  sx={{ width: "100%" }}
                  name="descripcion"
                  value={searchData.descripcion}
                  onChange={handleChange}
                  label="Descripción"
                  variant="outlined"
                  size="small"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  sx={{ width: "100%" }}
                  name="codigo"
                  value={searchData.codigo}
                  onChange={handleChange}
                  label="Código"
                  variant="outlined"
                  size="small"
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl sx={{ width: "100%" }}>
                  <InputLabel>Tipo fecha</InputLabel>
                  <Select
                    name="tipoFecha"
                    value={searchData.tipoFecha}
                    size="small"
                    label="Tipo fecha"
                    onChange={handleChange}
                  >
                    {tipoFechaOptions.map((item) => (
                      <MenuItem key={item.id} value={item.id}>
                        {item.descripcion}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              {/* <Grid item xs={12} md={3}>
                <TextField
                  sx={{ width: "100%" }}
                  name="tipoValor"
                  value={searchData.tipoValor}
                  onChange={handleChange}
                  label="Tipo valor"
                  variant="outlined"
                  size="small"
                  placeholder="Ej: PORCENTAJE"
                />
              </Grid> */}
              <Grid item xs={12} md={3}>
                <TextField
                  sx={{ width: "100%" }}
                  name="fechaDesde"
                  value={searchData.fechaDesde}
                  onChange={handleChange}
                  label="Desde"
                  variant="outlined"
                  size="small"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  sx={{ width: "100%" }}
                  name="fechaHasta"
                  value={searchData.fechaHasta}
                  onChange={handleChange}
                  label="Hasta"
                  variant="outlined"
                  size="small"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl sx={{ width: "100%" }}>
                  <InputLabel>Estado</InputLabel>
                  <Select
                    name="estado"
                    value={searchData.estado}
                    size="small"
                    label="Estado"
                    onChange={handleChange}
                  >
                    {estadoOptions.map((item) => (
                      <MenuItem key={item.id} value={item.id}>
                        {item.descripcion}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <Grid container spacing={1}>
                  <Grid item xs={6}>
                    <FormControl sx={{ width: "100%" }}>
                      <InputLabel>Ordenar por</InputLabel>
                      <Select
                        name="sortField"
                        value={searchData.sortField}
                        size="small"
                        label="Ordenar por"
                        onChange={handleChange}
                      >
                        {ordenarPorOptions.map((item) => (
                          <MenuItem key={item.id} value={item.id}>
                            {item.descripcion}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={6}>
                    <FormControl sx={{ width: "100%" }}>
                      <InputLabel>Orden</InputLabel>
                      <Select
                        name="sortOrder"
                        value={searchData.sortOrder}
                        size="small"
                        label="Orden"
                        onChange={handleChange}
                      >
                        {ordenOptions.map((item) => (
                          <MenuItem key={item.id} value={item.id}>
                            {item.descripcion}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Box>

          <Box sx={{ m: 1, p: 1 }}>
            <Grid container spacing={1}>
              <Grid item xs={12} md={2}>
                <Button
                  sx={{ width: "100%" }}
                  size="small"
                  variant="contained"
                  onClick={() => handleClickActionCrud({}, "create")}
                  startIcon={<AddIcon />}
                >
                  Nuevo
                </Button>
              </Grid>
              <Grid item xs={12} md={2}>
                <Button
                  sx={{ width: "100%" }}
                  size="small"
                  variant="outlined"
                  onClick={handleLimpiar}
                  startIcon={<CleaningServicesIcon />}
                >
                  Limpiar
                </Button>
              </Grid>
              <Grid item xs={12} md={2}>
                <Button
                  sx={{ width: "100%" }}
                  size="small"
                  variant="contained"
                  onClick={handleBuscar}
                  startIcon={<SearchIcon />}
                >
                  Buscar
                </Button>
              </Grid>
            </Grid>
          </Box>

          {isDoingSomething && <LinearProgress />}
          <MUIDataTable columns={tableColumns} data={itemsCrud} options={tableOptions} />
        </Box>
      </Paper>

      {openDialog && (
        <IndiceDialog
          onSave={handleDialogSave}
          onClose={() => setOpenDialog(false)}
          itemCrud={itemCrud}
          actionCrud={actionCrud}
          setMessage={setMessage}
          setShowMessage={setShowMessage}
        />
      )}

      {showMessage && (
        <Message
          showMessage={showMessage}
          setShowMessage={setShowMessage}
          message={message}
        />
      )}
    </>
  );
};

export default IndiceTable;
